import { type UseQueryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import type { ChartSettingsStore } from '@/components/Filters/ChartSettings/Store/store';
import type { Period } from '@/components/Filters/Period/store/store';
import type { StatisticsSeries } from '@/types/series';
import type { StatisticsData } from '@/types/statisticsApi';
import { factory } from '@/utils/queryFactory';
import { StatisticsQueryClient } from '../modules/ClientQuery';
import type { Compare, QueryProps } from '../types';
import { useCachedData } from './utils/useCachedData';

const useStatisticsQuery = <Series extends StatisticsSeries, C extends Compare>({
  ...props
}: QueryProps<Series, C> & {
  customPeriod?: Period;
  chartSettings?: ChartSettingsStore['chartSettings'];
  compareProps?: Partial<QueryProps<Series, C>>[];
}) => {
  const useSP = useSearchParams();
  const searchParams = useSP.toString();

  const { areaData, isFetchingAreaData, developerData, agencyData, seriesData } = useCachedData<Series, C>({
    resource: props.resource,
    ids: props.ids,
    areaIds: props.areaIds,
    compareProps: props.compareProps,
  });

  // TODO: `props` is a fresh object every render, so this useMemo currently rebuilds clientQuery on every
  // render (no real memoization). Fix as part of the ClientQuery stability refactor — memoize on a stable
  // key (e.g. searchParams + serialized props) and/or stop mutating `result` onto the instance.
  const clientQuery = useMemo(
    () =>
      new StatisticsQueryClient({
        searchParams,
        areaData,
        seriesData,
        developerData,
        agencyData,
        ...props,
      }),
    // biome-ignore lint/correctness/useExhaustiveDependencies: known unstable `props` dep — see TODO above; tracked for the ClientQuery refactor.
    [searchParams, areaData, seriesData, developerData, agencyData, props],
  );

  if (props.compareProps) {
    clientQuery.addComparisonQuery(props.compareProps);
  }
  const useBaseQuery = (
    options?: Partial<Omit<UseQueryOptions<StatisticsData<Series, C>>, 'queryKey' | 'queryFn'>>,
  ) => {
    const base = factory.statistics.query<Series, C>({
      params: clientQuery.getUrlParams(),
      resource: clientQuery.resource,
      fetchMethod: clientQuery.fetchMethod,
    });
    const res = useQuery({ ...base, ...options } as typeof base);

    if (res.data) {
      clientQuery.result = res.data;
    }

    return res;
  };

  clientQuery.useBaseQuery = useBaseQuery;

  const useComparisonQueries = () => {
    const res = useQueries({
      queries:
        clientQuery.comparisonQueries?.map((compareQuery) => {
          return factory.statistics.queryCompare<Series, C>({
            params: compareQuery.getUrlParams(),
            resource: compareQuery.resource,
            fetchMethod: compareQuery.fetchMethod,
          });
        }) ?? [],
    });

    clientQuery.comparisonQueries = clientQuery.comparisonQueries?.map((query, i) => {
      if (res[i].data) {
        query.result = res[i].data;
      }

      return query;
    });

    return res;
  };
  clientQuery.useComparisonQueries = useComparisonQueries;

  return { clientQuery, isFetchingAreaData };
};

export { useStatisticsQuery };
